import React, { useContext, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import {
  TEACHERS_ACTIVITY_STREAM_URL,
  TEACHERS_ASSIGNMENTS_URL,
  TEACHERS_BULK_UPLOAD_ASSIGN_ASSIGNMENT_URL,
  TEACHERS_BULK_UPLOAD_NAME_REGION_SELECT_URL,
  TEACHERS_BULK_UPLOAD_PAGE_CONFIRMATION_URL,
  TEACHERS_BULK_UPLOAD_SUBMISSIONS_MATCH_URL,
  TEACHERS_BULK_UPLOAD_SUCCESS_URL,
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_URL,
  TEACHERS_CAPTURE_URL,
  TEACHERS_MEETINGS_LIST_URL,
  TEACHERS_MEETING_FEED_URL,
  TEACHERS_MEETING_SORT_URL,
  TEACHERS_PERMISSION_DENIED_URL,
  TEACHERS_STUDENTS_LIST,
  TEACHERS_STUDENT_GROUP_LIST_URL,
  TEACHERS_STUDENT_GROUP_URL,
  TEACHERS_STUDENT_PORTFOLIO_ASSIGNMENT_DETAILS_URL,
  TEACHERS_STUDENT_PORTFOLIO_URL,
  TEACHERS_SUBSCRIPTIONS_URL,
  TEACHERS_TEAM_DETAIL_URL
} from 'config/urls';

import ConfirmPages from 'pages/Teachers/BulkUpload/ConfirmPages';
import NameRegionSelect from 'pages/Teachers/BulkUpload/NameRegionSelect';
import StudentsConfirmation from 'pages/Teachers/BulkUpload/StudentsConfirmation';
import BulkUploadSuccess from 'pages/Teachers/BulkUpload/Success';
import TeamDetail from 'pages/Teachers/TeamDetail';
import { TeacherPageLayout, UsageDialog } from 'pages/Teachers/shared';
import { parseUsage } from 'pages/Teachers/shared/utils';
import { useCleverSync } from 'utils/integrations/clever';
import { useGoogleClassroomSync } from 'utils/integrations/googleClassroom';

import IntegrationsSyncContext from 'components/IntegrationsSyncContext';
import TeacherRequired from 'components/TeacherRequired';
import UsageContext from 'components/UsageContext';
import UserContext from 'components/UserContext';

import ActivityStream from './ActivityStream';
import Assignments from './Assignments';
import AssignAssignment from './BulkUpload/AssignAssignment';
import WorkUpload from './BulkUpload/WorkUpload';
import Capture from './Capture';
import MeetingFeed from './MeetingFeed';
import MeetingSort from './MeetingSort';
import MeetingsList from './MeetingsList';
import NoPermission from './NoPermission';
import StudentGroupList from './StudentGroupList';
import StudentPortfolio from './StudentPortfolio';
import StudentPortfolioAssignmentDetails from './StudentPortfolioAssignmentDetails';
import StudentsList from './StudentsList';
import Subscriptions from './Subscriptions';
import Tutoring from './Tutoring';

const useUsageDialog = () => {
  const { user } = useContext(UserContext);
  const usage = parseUsage(user.usage);

  const [usageDialog, setUsageDialog] = useState(
    (usage && !usage.hasAccess) || false
  );

  const openDialog = () => {
    const shouldOpen = (!usageDialog && usage && !usage.hasAccess) || false;

    if (shouldOpen) {
      setUsageDialog(true);
    }
  };
  const closeDialog = () => {
    setUsageDialog(false);
  };

  return { usage, dialog: usageDialog, openDialog, closeDialog };
};

const Teachers = () => {
  const { dialog, openDialog, closeDialog, usage } = useUsageDialog();

  const googleClassroom = useGoogleClassroomSync();
  const clever = useCleverSync();

  return (
    <UsageContext.Provider value={{ usage, openUsageDialog: openDialog }}>
      <IntegrationsSyncContext.Provider value={{ googleClassroom, clever }}>
        <TeacherPageLayout>
          <Switch>
            <Route path={TEACHERS_ASSIGNMENTS_URL} component={Assignments} />
            <Route
              exact
              path={TEACHERS_ACTIVITY_STREAM_URL}
              component={ActivityStream}
            />
            <Route
              path={TEACHERS_SUBSCRIPTIONS_URL}
              component={Subscriptions}
            />
            <Route exact path={TEACHERS_CAPTURE_URL} component={Capture} />
            <Route
              exact
              path={TEACHERS_STUDENTS_LIST}
              component={StudentsList}
            />
            <Route
              exact
              path={TEACHERS_STUDENT_PORTFOLIO_URL}
              component={StudentPortfolio}
            />
            <Route
              exact
              path={TEACHERS_STUDENT_GROUP_LIST_URL}
              component={StudentGroupList}
            />
            <Route
              exact
              path={TEACHERS_STUDENT_GROUP_URL}
              component={Tutoring}
            />
            <Route
              exact
              path={TEACHERS_STUDENT_PORTFOLIO_ASSIGNMENT_DETAILS_URL}
              component={StudentPortfolioAssignmentDetails}
            />
            <Route
              exact
              path={TEACHERS_TEAM_DETAIL_URL}
              component={TeamDetail}
            />
            <Route
              exact
              path={TEACHERS_MEETINGS_LIST_URL}
              component={MeetingsList}
            />
            <Route
              exact
              path={TEACHERS_MEETING_FEED_URL}
              component={MeetingFeed}
            />
            <Route
              exact
              path={TEACHERS_MEETING_SORT_URL}
              component={MeetingSort}
            />
            <Route
              exact
              path={TEACHERS_PERMISSION_DENIED_URL}
              component={NoPermission}
            />
            <Route
              exact
              path={TEACHERS_BULK_UPLOAD_WORK_UPLOAD_URL}
              component={WorkUpload}
            />
            <Route
              exact
              path={TEACHERS_BULK_UPLOAD_ASSIGN_ASSIGNMENT_URL}
              component={AssignAssignment}
            />
            <Route
              exact
              path={TEACHERS_BULK_UPLOAD_PAGE_CONFIRMATION_URL}
              component={ConfirmPages}
            />
            <Route
              exact
              path={TEACHERS_BULK_UPLOAD_NAME_REGION_SELECT_URL}
              component={NameRegionSelect}
            />
            <Route
              exact
              path={TEACHERS_BULK_UPLOAD_SUBMISSIONS_MATCH_URL}
              component={StudentsConfirmation}
            />
            <Route
              exact
              path={TEACHERS_BULK_UPLOAD_SUCCESS_URL}
              component={BulkUploadSuccess}
            />
            <Route path="*">
              <Redirect to="/" />
            </Route>
          </Switch>
          {dialog && <UsageDialog usage={usage} onClose={closeDialog} />}
        </TeacherPageLayout>
      </IntegrationsSyncContext.Provider>
    </UsageContext.Provider>
  );
};

export default TeacherRequired(Teachers);
